import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtMXN, fmtDate } from "@/lib/api";
import SignaturePad from "@/components/SignaturePad";
import { Printer, PenLine, CheckCircle2, FileDown } from "lucide-react";
import { toast } from "sonner";

const NAVY = "#1e2a5a";

export default function PublicPieceworkReceipt() {
  const { token } = useParams();
  const [rc, setRc] = useState(null);
  const [error, setError] = useState("");
  const [signOpen, setSignOpen] = useState(false);
  const [signName, setSignName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/public/piecework-receipts/${token}`);
      setRc(data);
      setSignName(data.signed_name || data.worker?.name || "");
    } catch (err) {
      setError(err.response?.data?.detail || "Enlace no válido");
    }
  };
  useEffect(() => { load(); }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitSignature = async (dataUrl) => {
    setSaving(true);
    try {
      await api.post(`/public/piecework-receipts/${token}/sign`, { signature: dataUrl, name: signName });
      toast.success("¡Recibo firmado, gracias!");
      setSignOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo guardar la firma");
    }
    setSaving(false);
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/public/piecework-receipts/${token}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "recibo_destajo.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el PDF"); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center" style={{ background: "#0a0f1d" }}>
        <div>
          <div className="text-4xl mb-3">⚠️</div>
          <div className="text-slate-200 font-semibold">{error}</div>
          <div className="text-slate-500 text-sm mt-2">Solicita un nuevo enlace a Corporation Inconti.</div>
        </div>
      </div>
    );
  }
  if (!rc) return <div className="min-h-screen flex items-center justify-center text-slate-400" style={{ background: "#0a0f1d" }}>Cargando…</div>;

  const p = rc.payment || {};
  const sig = rc.signature;

  return (
    <div className="min-h-screen" style={{ background: "#0a0f1d" }} data-testid="public-piecework-receipt-page">
      <div className="no-print sticky top-0 z-10 px-4 py-3 flex items-center justify-between gap-2"
        style={{ borderBottom: "1px solid rgba(148,163,184,0.2)", background: "rgba(5,8,18,0.9)", backdropFilter: "blur(8px)", paddingTop: "calc(0.75rem + env(safe-area-inset-top))" }}>
        <div className="text-xs text-slate-300 uppercase tracking-widest font-semibold">Recibo de pago · Inconti</div>
        <div className="flex gap-2">
          <button onClick={downloadPdf} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 text-slate-200" style={{ border: "1px solid rgba(148,163,184,0.35)" }}>
            <FileDown className="w-4 h-4" /> PDF
          </button>
          <button onClick={() => window.print()} className="px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2 text-slate-200" style={{ border: "1px solid rgba(148,163,184,0.35)" }}>
            <Printer className="w-4 h-4" /> Imprimir
          </button>
        </div>
      </div>

      <div className="py-6 px-2 sm:px-4 flex justify-center">
        <div className="bg-white text-slate-900 w-full max-w-[720px] shadow-xl" style={{ fontFamily: "Helvetica, Arial, sans-serif" }}>
          <div className="px-5 py-3 flex items-center justify-between text-white" style={{ background: NAVY }}>
            <div className="font-bold text-sm tracking-wide">INCONTI // CORPORATION</div>
            <div className="text-[11px]">Folio: {(p.id || "").slice(0, 8).toUpperCase()}</div>
          </div>
          <div className="px-5 py-4">
            <div className="text-lg font-bold" style={{ color: NAVY }}>RECIBO DE PAGO · DESTAJO</div>
            <div className="text-[11px] text-slate-500 mb-3">Comprobante de pago por trabajo a destajo · {rc.payment_type_label}</div>

            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] border border-slate-300 p-3 mb-4">
              <div><span className="font-bold text-slate-600">DESTAJISTA: </span>{rc.worker?.name}</div>
              <div><span className="font-bold text-slate-600">FECHA DE PAGO: </span>{fmtDate(p.payment_date)}</div>
              <div><span className="font-bold text-slate-600">PROYECTO: </span>{rc.project ? `${rc.project.code} · ${rc.project.name}` : "—"}</div>
              <div><span className="font-bold text-slate-600">FORMA DE PAGO: </span>{rc.pm_label}</div>
              <div className="col-span-2"><span className="font-bold text-slate-600">TRABAJO: </span>{rc.contract?.description || "—"}</div>
              <div><span className="font-bold text-slate-600">UNIDADES: </span>{p.units_paid} {rc.contract?.unit} × {fmtMXN(p.unit_price)}</div>
              <div><span className="font-bold text-slate-600">TIPO: </span>{rc.payment_type_label}</div>
            </div>

            <div className="border-2 text-center py-3 mb-4" style={{ borderColor: NAVY }} data-testid="receipt-amount">
              <div className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Monto pagado</div>
              <div className="text-2xl font-bold text-green-700">{fmtMXN(p.amount)}</div>
              <div className="text-[10px] text-slate-500 px-4">{rc.amount_words}</div>
            </div>

            <div className="text-[12px] font-bold mb-1" style={{ color: NAVY }}>ESTADO DEL CONTRATO (después de este pago)</div>
            <table className="w-full text-[11px] border-collapse mb-5">
              <thead>
                <tr className="text-white" style={{ background: NAVY }}>
                  {["TOTAL DEL CONTRATO", "PAGADO ACUMULADO", "SALDO POR PAGAR"].map((h) => <th key={h} className="px-2 py-1.5 border border-slate-300">{h}</th>)}
                </tr>
              </thead>
              <tbody>
                <tr className="text-center font-bold">
                  <td className="px-2 py-1.5 border border-slate-300">{fmtMXN(rc.acc?.contract_total)}</td>
                  <td className="px-2 py-1.5 border border-slate-300 text-green-700">{fmtMXN(rc.acc?.paid_amount)}</td>
                  <td className="px-2 py-1.5 border border-slate-300 text-amber-700">{fmtMXN(rc.acc?.balance)}</td>
                </tr>
                <tr className="text-center text-slate-500">
                  <td className="px-2 py-1 border border-slate-300">{rc.contract?.total_units} {rc.contract?.unit}</td>
                  <td className="px-2 py-1 border border-slate-300">{rc.acc?.paid_units} {rc.contract?.unit}</td>
                  <td className="px-2 py-1 border border-slate-300">{rc.acc?.balance_units} {rc.contract?.unit}</td>
                </tr>
              </tbody>
            </table>

            {p.notes && <div className="text-[11px] text-slate-600 mb-4"><span className="font-bold">NOTAS: </span>{p.notes}</div>}

            <div className="grid grid-cols-2 gap-6 mb-4">
              <div className="text-center pt-10">
                <div className="border-t border-slate-400 pt-1 text-[10px] font-bold text-slate-600 mx-4">ELABORÓ · INCONTI</div>
              </div>
              <div className="text-center" data-testid="receipt-sign-area">
                {sig?.signature ? (
                  <div>
                    <img src={sig.signature} alt="Firma" className="mx-auto h-16 object-contain" />
                    <div className="border-t border-slate-400 pt-1 text-[10px] font-bold text-slate-600 mx-4">
                      FIRMADO DIGITALMENTE · {(sig.signed_name || "").toUpperCase()} · {fmtDate(sig.signed_at)}
                    </div>
                    <div className="no-print mt-1 text-[11px] text-green-700 font-semibold flex items-center justify-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Firmado de conformidad</div>
                  </div>
                ) : (
                  <div>
                    <div className="pt-6" />
                    <div className="border-t border-slate-400 pt-1 text-[10px] font-bold text-slate-600 mx-4">RECIBÍ DE CONFORMIDAD · {(rc.worker?.name || "").toUpperCase()}</div>
                    <div className="no-print mt-3">
                      <input data-testid="receipt-sign-name" value={signName} onChange={(e) => setSignName(e.target.value)} placeholder="Nombre de quien firma"
                        className="w-full max-w-[240px] mx-auto block border border-slate-300 rounded-sm px-2 py-1.5 text-[12px] mb-2" />
                      <button data-testid="receipt-sign-btn" onClick={() => setSignOpen(true)}
                        className="px-4 py-2 text-xs uppercase tracking-widest font-bold rounded-sm text-white inline-flex items-center gap-2" style={{ background: NAVY }}>
                        <PenLine className="w-4 h-4" /> Firmar de recibido
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center text-[9px] text-slate-400 pb-4">Documento generado por el sistema de gestión INCONTI · No es un comprobante fiscal (CFDI).</div>
          </div>
        </div>
      </div>

      {signOpen && (
        <SignaturePad title={`Firma de ${signName || rc.worker?.name}`} saving={saving}
          onConfirm={submitSignature} onClose={() => setSignOpen(false)} />
      )}
    </div>
  );
}
